import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Package, Search, Loader2, CheckCircle, Truck, Download, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

const STATUS_CONFIG = {
  paid: { label: "Order Placed", icon: CheckCircle, color: "text-[#7A9E7E]" },
  shipped: { label: "Shipped", icon: Truck, color: "text-[#A87C6D]" },
  delivered: { label: "Delivered", icon: Package, color: "text-[#7A9E7E]" },
  completed: { label: "Completed", icon: CheckCircle, color: "text-[#7A9E7E]" },
};

export default function OrderLookup() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const handleLookup = async (e) => {
    e.preventDefault();
    
    if (!orderId.trim() || !email.trim()) {
      toast.error("Please enter both order ID and email");
      return;
    }

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const response = await fetch(`${API}/store/orders/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId.trim(),
          email: email.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      } else {
        const data = await response.json();
        setError(data.detail || "Order not found");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const statusConfig = order ? (STATUS_CONFIG[order.status] || STATUS_CONFIG.paid) : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E6E2DD]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/store" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Store</span>
          </Link>
          
          <h1 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Track Order
          </h1>
          
          <div className="w-20" />
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-6 py-12 w-full">
        {!order ? (
          <>
            {/* Lookup Form */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-[#A87C6D]" />
              </div>
              <h2 
                className="text-2xl font-light text-[#2D2A26] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Look up your order
              </h2>
              <p className="text-[#6E6A66]">
                Enter your order ID and email address to view your order status.
              </p>
            </div>

            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <label className="block text-sm text-[#6E6A66] mb-2">Order ID</label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="e.g., order_abc123def456"
                  className="w-full px-4 py-3 border border-[#E6E2DD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] bg-white"
                  data-testid="order-id-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#6E6A66] mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="The email you used at checkout"
                  className="w-full px-4 py-3 border border-[#E6E2DD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] bg-white"
                  data-testid="email-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-[#C8553D] bg-[#C8553D]/10 px-4 py-3 rounded-xl">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                data-testid="lookup-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find My Order
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 p-4 bg-[#F5F2ED] rounded-xl text-center">
              <p className="text-sm text-[#6E6A66]">
                Have an account?{" "}
                <Link to="/login" className="text-[#A87C6D] hover:underline">
                  Sign in
                </Link>{" "}
                to view all your orders.
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Order Details */}
            <div className="mb-6 flex items-center justify-between">
              <button
                onClick={() => setOrder(null)}
                className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]"
              >
                <ArrowLeft className="w-4 h-4" />
                Search another order
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E6E2DD] overflow-hidden">
              {/* Order Header */}
              <div className="px-6 py-4 border-b border-[#E6E2DD] bg-[#FDFBF7]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#A8A4A0]">Order</p>
                    <p className="font-medium text-[#2D2A26]">#{order.order_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#A8A4A0]">Date</p>
                    <p className="text-[#6E6A66]">{formatDate(order.created_at)}</p>
                  </div>
                  <div className={`flex items-center gap-2 ${statusConfig?.color}`}>
                    {StatusIcon && <StatusIcon className="w-5 h-5" />}
                    <span className="font-medium">{statusConfig?.label}</span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="px-6 py-4 space-y-3">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-[#2D2A26]">{item.name}</span>
                      <span className="text-[#A8A4A0]">x{item.quantity}</span>
                      {item.product_type === "digital" && (
                        <span className="text-xs px-2 py-0.5 bg-[#A87C6D]/10 text-[#A87C6D] rounded-full">
                          Digital
                        </span>
                      )}
                    </div>
                    <span className="text-[#6E6A66]">
                      ${(item.unit_price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                
                {order.has_physical && (
                  <div className="flex items-center justify-between text-[#A8A4A0]">
                    <span>Shipping</span>
                    <span>$7.00</span>
                  </div>
                )}
                
                <div className="pt-3 border-t border-[#E6E2DD] flex justify-between font-medium">
                  <span className="text-[#2D2A26]">Total</span>
                  <span className="text-[#2D2A26]">${order.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Digital Downloads */}
              {order.has_digital && order.items?.some(i => i.product_type === "digital" && i.digital_download_url) && (
                <div className="px-6 py-4 bg-[#F5F2ED] border-t border-[#E6E2DD]">
                  <div className="flex items-center gap-2 text-[#A87C6D] mb-3">
                    <Download className="w-5 h-5" />
                    <span className="font-medium">Digital Downloads</span>
                  </div>
                  <div className="space-y-2">
                    {order.items.filter(i => i.product_type === "digital" && i.digital_download_url).map((item, idx) => (
                      <a
                        key={idx}
                        href={item.digital_download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#6E6A66] hover:text-[#A87C6D]"
                      >
                        <Download className="w-4 h-4" />
                        <span>{item.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Tracking Info */}
              {order.has_physical && order.tracking_number && (
                <div className="px-6 py-4 bg-[#F5F2ED] border-t border-[#E6E2DD]">
                  <div className="flex items-center gap-2 text-[#6E6A66]">
                    <Truck className="w-5 h-5" />
                    <span>Tracking: {order.tracking_number}</span>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              {order.has_physical && order.shipping_address && (
                <div className="px-6 py-4 border-t border-[#E6E2DD]">
                  <p className="text-sm text-[#A8A4A0] mb-1">Shipping to</p>
                  <p className="text-[#6E6A66] text-sm">
                    {order.shipping_address.name}, {order.shipping_address.street}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                  </p>
                </div>
              )}

              {/* Status Message */}
              {order.status === "paid" && order.has_physical && (
                <div className="px-6 py-4 border-t border-[#E6E2DD] bg-[#7A9E7E]/5">
                  <p className="text-sm text-[#6E6A66]">
                    Your order is being prepared. You'll receive an email with tracking information once it ships.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-[#A8A4A0] mb-4">
                Questions about your order? Contact us at support@thesoftritual.com
              </p>
              <Link to="/store">
                <Button
                  variant="outline"
                  className="rounded-full border-[#E6E2DD] text-[#6E6A66]"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
